/**
 * HRReports.jsx — A-HR §2: HR Reports Pack
 * Headcount & Master + Absenteeism reports
 */
import React, { useState, useEffect } from 'react';
import { Users, AlertTriangle, Download, Landmark, UserCog } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';
import { PfShortfallReport, DeptMismatchReport, PayrollGrossMismatchReport, PfOptOutComplianceReport } from './HRDataChecks';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HRReports() {
  const [tab, setTab] = useState('headcount');

  return (
    <div className="space-y-4" data-testid="hr-reports-page">
      <div className="flex flex-wrap gap-2">
        {[['headcount', 'Headcount & Master', Users], ['absenteeism', 'Absenteeism', AlertTriangle],
          ['pf-shortfall', 'PF Shortfall (Basic + DA)', Landmark], ['dept-mismatch', 'Login vs Employee Dept', UserCog],
          ['gross-mismatch', 'Payroll Gross Mismatch', AlertTriangle],
          ['pf-optout', 'PF Opt-Out Compliance', Landmark]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`reports-tab-${id}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-sm font-semibold transition-colors ${tab === id ? 'bg-[#3b4872] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#3b4872]/50'}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>
      {tab === 'headcount' && <HeadcountReport />}
      {tab === 'absenteeism' && <AbsenteeismReport />}
      {tab === 'pf-shortfall' && <PfShortfallReport />}
      {tab === 'dept-mismatch' && <DeptMismatchReport />}
      {tab === 'gross-mismatch' && <PayrollGrossMismatchReport />}
      {tab === 'pf-optout' && <PfOptOutComplianceReport />}
    </div>
  );
}

function HeadcountReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ department: '', designation: '', grade: '', status: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.department) params.department = filters.department;
      if (filters.designation) params.designation = filters.designation;
      if (filters.grade) params.grade = filters.grade;
      if (filters.status) params.status = filters.status;
      setData(await apiGet('/hr/reports/headcount', params));
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const exportXlsx = () => {
    const p = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)));
    const tok = localStorage.getItem('access_token');
    const url = `${API}/api/hr/reports/headcount/export?${p}`;
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.blob()).then(blob => {
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'headcount.xlsx'; a.click();
      }).catch(() => toast.error('Export failed'));
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        {[['department', 'Department'], ['designation', 'Designation'], ['grade', 'Grade'], ['status', 'Status']].map(([k, l]) => (
          <div key={k}>
            <label className="block text-xs font-semibold text-slate-500 mb-1">{l}</label>
            <input value={filters[k]} onChange={e => setFilters({ ...filters, [k]: e.target.value })}
              placeholder={`All ${l.toLowerCase()}s`} className="border rounded px-2 py-1.5 text-sm w-36"
              data-testid={`headcount-filter-${k}`} />
          </div>
        ))}
        <button onClick={load} className="px-3 py-1.5 bg-[#3b4872] text-white text-sm rounded" data-testid="headcount-apply">Apply</button>
        <button onClick={exportXlsx} className="px-3 py-1.5 border border-[#3b4872] text-[#3b4872] text-sm rounded flex items-center gap-1" data-testid="headcount-export">
          <Download size={13} /> Export xlsx
        </button>
        {data && <span className="text-xs text-slate-500 ml-auto">{data.total} employees</span>}
      </div>
      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(data.summary?.by_department || {}).slice(0, 4).map(([dept, count]) => (
            <div key={dept} className="bg-white border border-slate-200 rounded-sm p-3">
              <div className="text-xs text-slate-500 truncate">{dept}</div>
              <div className="text-2xl font-bold text-[#3b4872]">{count}</div>
            </div>
          ))}
        </div>
      )}
      {/* Table */}
      <div className="border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              {['Code', 'Name', 'Department', 'Designation', 'Grade', 'Status', 'Employment Status', 'DOJ'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading…</td></tr>
              : (data?.employees || []).length === 0
                ? <tr><td colSpan={8} className="text-center py-8 text-slate-400">No employees match the filters.</td></tr>
                : (data?.employees || []).map((e, i) => (
                  <tr key={e.id} className="hover:bg-slate-50" data-testid={`headcount-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-xs">{e.emp_id}</td>
                    <td className="px-3 py-2 font-medium">{e.name}</td>
                    <td className="px-3 py-2 text-slate-600">{e.department}</td>
                    <td className="px-3 py-2 text-slate-600">{e.designation}</td>
                    <td className="px-3 py-2">{e.grade}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded-sm font-semibold ${e.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{e.status}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{e.employment_status}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{e.date_of_joining}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AbsenteeismReport() {
  const today = new Date();
  const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [dept, setDept] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [threshold, setThreshold] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { apiGet('/projects').then(r => setProjects(Array.isArray(r) ? r : (r?.projects || []))).catch(() => {}); }, []);

  const load = async () => {
    if (!from || !to) { toast.error('Select a date range'); return; }
    setLoading(true);
    try {
      const params = { from_date: from, to_date: to, threshold_days: threshold };
      if (dept) params.department = dept;
      if (projectId) params.project_id = projectId;
      setData(await apiGet('/hr/reports/absenteeism', params));
    } catch { toast.error('Failed to load absenteeism report'); }
    finally { setLoading(false); }
  };

  const exportXlsx = () => {
    const p = new URLSearchParams({ from_date: from, to_date: to, threshold_days: threshold, ...(dept ? { department: dept } : {}), ...(projectId ? { project_id: projectId } : {}) });
    const tok = localStorage.getItem('access_token');
    fetch(`${API}/api/hr/reports/absenteeism/export?${p}`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.blob()).then(blob => {
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'absenteeism.xlsx'; a.click();
      }).catch(() => toast.error('Export failed'));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border rounded px-2 py-1.5 text-sm" data-testid="absent-date-from" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border rounded px-2 py-1.5 text-sm" data-testid="absent-date-to" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Department</label>
          <input value={dept} onChange={e => setDept(e.target.value)} placeholder="All departments" className="border rounded px-2 py-1.5 text-sm w-36" data-testid="absent-dept-filter" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Project</label>
          <select value={projectId} onChange={e => setProjectId(e.target.value)} className="border rounded px-2 py-1.5 text-sm w-40" data-testid="absent-project-filter">
            <option value="">All projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Threshold (A+LWP days &gt;)</label>
          <input type="number" min="0" value={threshold} onChange={e => setThreshold(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm w-20" data-testid="absent-threshold" />
        </div>
        <button onClick={load} className="px-3 py-1.5 bg-[#3b4872] text-white text-sm rounded" data-testid="absent-apply">Apply</button>
        {data && data.total > 0 && (
          <button onClick={exportXlsx} className="px-3 py-1.5 border border-[#3b4872] text-[#3b4872] text-sm rounded flex items-center gap-1" data-testid="absent-export">
            <Download size={13} /> Export xlsx
          </button>
        )}
        {data && <span className="text-xs text-slate-500 ml-auto">{data.total} employees above threshold</span>}
      </div>
      <div className="border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              {['Code', 'Name', 'Department', 'Designation', 'Reporting Manager', 'Absent (A)', 'LWP', 'Total', '% Absent'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>
              : !data ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">Select a period and click Apply.</td></tr>
                : (data?.employees || []).length === 0
                  ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">No employees exceeded the threshold.</td></tr>
                  : (data.employees || []).map((e, i) => (
                    <tr key={e.employee_id} className={`hover:bg-slate-50 ${e.total_absent_days > 10 ? 'bg-rose-50' : ''}`} data-testid={`absent-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-xs">{e.emp_id}</td>
                      <td className="px-3 py-2 font-medium">{e.name}</td>
                      <td className="px-3 py-2 text-slate-600">{e.department}</td>
                      <td className="px-3 py-2 text-slate-600">{e.designation}</td>
                      <td className="px-3 py-2 text-slate-500">{e.reporting_manager || '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-rose-700">{e.absent_days}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-700">{e.lwp_days}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{e.total_absent_days}</td>
                      <td className="px-3 py-2 text-right font-mono">{e.absent_pct}%</td>
                    </tr>
                  ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
